import React, { useEffect, useMemo, useState } from "react";
import AdminLayout from "../../masterLayout/AdminLayout";
import AdminPageHeader from "../../components/admin/AdminPageHeader";
import { adminApi, handleAdminLogoutIfNeeded } from "../../utils/adminApi";

const empty = {
  app_name: "",
  youtube_video: "",
  meta: "",
  currency_symbol: "",
  exchange_rate: "",
  smtp_host: "",
  smtp_port: "",
  smtp_email: "",
  smtp_pass: "",
  is_custom_home: 0,
  custom_home_url: "",
  facebook_app_id: "",
  facebook_app_secret: "",
  facebook_verify_token: "",
  facebook_login_config_id: "",
  whatsapp_login_config_id: "",
};

function randomVerifyToken() {
  const bytes = new Uint8Array(16);
  crypto.getRandomValues(bytes);
  return `axc_${Array.from(bytes, (b) => b.toString(16).padStart(2, "0")).join("")}`;
}

const AdminSettings = () => {
  const [form, setForm] = useState(empty);
  const [logoFile, setLogoFile] = useState(null);
  const [msg, setMsg] = useState("");
  const [saving, setSaving] = useState(false);
  const [showSecret, setShowSecret] = useState(false);

  const [oauthCallbackUrl, setOauthCallbackUrl] = useState(() => {
    const base = (process.env.REACT_APP_API_URL || "").replace(/\/$/, "");
    return base ? `${base}/webhook/oauth/callback` : "/webhook/oauth/callback";
  });
  const oauthAppDomain = useMemo(() => {
    try {
      return new URL(oauthCallbackUrl).hostname;
    } catch {
      return "";
    }
  }, [oauthCallbackUrl]);

  const metaReady = Boolean(
    String(form.facebook_app_id || "").trim() &&
      String(form.facebook_app_secret || "").trim() &&
      String(form.facebook_verify_token || "").trim() &&
      String(form.facebook_login_config_id || "").trim()
  );

  useEffect(() => {
    document.title = "Admin Settings";
    (async () => {
      const res = await adminApi({ url: "web/get-web-public", method: "GET" });
      if (handleAdminLogoutIfNeeded(res)) return;
      if (res.success && res.data) {
        setForm({
          app_name: res.data.app_name || "",
          youtube_video: res.data.youtube_video || "",
          meta: res.data.meta || "",
          currency_symbol: res.data.currency_symbol || "",
          exchange_rate: res.data.exchange_rate || "",
          smtp_host: res.data.smtp_host || "",
          smtp_port: res.data.smtp_port || "",
          smtp_email: res.data.smtp_email || "",
          smtp_pass: res.data.smtp_pass || "",
          is_custom_home: Number(res.data.is_custom_home) || 0,
          custom_home_url: res.data.custom_home_url || "",
          facebook_app_id: res.data.facebook_app_id || "",
          facebook_app_secret: res.data.facebook_app_secret || "",
          facebook_verify_token: res.data.facebook_verify_token || "",
          facebook_login_config_id: res.data.facebook_login_config_id || "",
          whatsapp_login_config_id: res.data.whatsapp_login_config_id || "",
        });
        if (res.data.oauth_callback_url) {
          setOauthCallbackUrl(res.data.oauth_callback_url);
        }
      }
    })().catch(console.error);
  }, []);

  const setField = (key, value) => setForm((f) => ({ ...f, [key]: value }));

  const copyText = async (text, label) => {
    try {
      await navigator.clipboard.writeText(text);
      setMsg(`${label} copied`);
    } catch {
      setMsg(`Could not copy ${label}`);
    }
  };

  const save = async (e) => {
    e.preventDefault();
    setSaving(true);
    setMsg("");
    try {
      const body = new FormData();
      Object.entries(form).forEach(([k, v]) => body.append(k, v ?? ""));
      if (logoFile) body.append("file", logoFile);

      const res = await adminApi({
        url: "web/update-app-config",
        method: "POST",
        body,
        withoutStringify: true,
      });
      if (handleAdminLogoutIfNeeded(res)) return;
      setMsg(res.msg || (res.success ? "Settings updated" : "Failed"));
      if (res.success && res.data) {
        setForm((f) => ({
          ...f,
          facebook_app_id: res.data.facebook_app_id || f.facebook_app_id,
          facebook_app_secret: res.data.facebook_app_secret || f.facebook_app_secret,
          facebook_verify_token: res.data.facebook_verify_token || f.facebook_verify_token,
          facebook_login_config_id:
            res.data.facebook_login_config_id || f.facebook_login_config_id,
          whatsapp_login_config_id:
            res.data.whatsapp_login_config_id || f.whatsapp_login_config_id,
          app_name: res.data.app_name || f.app_name,
        }));
      }
    } finally {
      setSaving(false);
    }
  };

  return (
    <AdminLayout>
      <div className="admin-ui">
        <AdminPageHeader
          title="Site settings"
          subtitle="App branding, currency, SMTP, Meta Connect, and home URL configuration."
        />
        {msg ? <div className="admin-alert admin-alert-info">{msg}</div> : null}
        <div className="admin-form-card">
          <div className="admin-form-head">
            <h6>Application config</h6>
            <p>Changes apply to the public site, emails, and Webhooks → Connect.</p>
          </div>
          <form onSubmit={save}>
            <div className="admin-form-section">
              <p className="admin-form-section-title">General</p>
              <div className="row g-3">
                {[
                  ["app_name", "App name", "text"],
                  ["youtube_video", "YouTube video URL", "text"],
                  ["meta", "Meta description", "text"],
                  ["currency_symbol", "Currency symbol", "text"],
                  ["exchange_rate", "Exchange rate", "text"],
                ].map(([key, label, type]) => (
                  <div className="col-md-6" key={key}>
                    <label className="form-label">{label}</label>
                    <input
                      className="form-control"
                      type={type}
                      value={form[key]}
                      onChange={(e) => setField(key, e.target.value)}
                    />
                  </div>
                ))}
                <div className="col-md-6">
                  <label className="form-label">Logo (optional)</label>
                  <input
                    type="file"
                    accept="image/*"
                    className="form-control"
                    onChange={(e) => setLogoFile(e.target.files?.[0] || null)}
                  />
                </div>
              </div>
            </div>

            <div className="admin-form-section">
              <p className="admin-form-section-title">SMTP</p>
              <div className="row g-3">
                {[
                  ["smtp_host", "SMTP host", "text"],
                  ["smtp_port", "SMTP port", "text"],
                  ["smtp_email", "SMTP email", "email"],
                  ["smtp_pass", "SMTP password", "password"],
                ].map(([key, label, type]) => (
                  <div className="col-md-6" key={key}>
                    <label className="form-label">{label}</label>
                    <input
                      className="form-control"
                      type={type}
                      value={form[key]}
                      onChange={(e) => setField(key, e.target.value)}
                    />
                  </div>
                ))}
              </div>
            </div>

            <div className="admin-form-section">
              <p className="admin-form-section-title">Home URL</p>
              <div className="row g-3">
                <div className="col-md-6">
                  <label className="form-label">Custom home URL</label>
                  <input
                    className="form-control"
                    value={form.custom_home_url}
                    onChange={(e) => setField("custom_home_url", e.target.value)}
                    placeholder="https://…"
                  />
                </div>
                <div className="col-md-6 d-flex align-items-end">
                  <div className="form-check">
                    <input
                      className="form-check-input"
                      type="checkbox"
                      id="custom-home"
                      checked={!!Number(form.is_custom_home)}
                      onChange={(e) => setField("is_custom_home", e.target.checked ? 1 : 0)}
                    />
                    <label className="form-check-label" htmlFor="custom-home">
                      Use custom home URL
                    </label>
                  </div>
                </div>
              </div>
            </div>

            <div className="admin-form-section">
              <div className="d-flex flex-wrap align-items-center justify-content-between gap-2 mb-2">
                <p className="admin-form-section-title mb-0">Meta / Facebook Login (Messenger & Instagram)</p>
                <span
                  className={`badge ${metaReady ? "text-bg-success" : "text-bg-warning"}`}
                  style={{ fontWeight: 500 }}
                >
                  {metaReady ? "Connect ready" : "Needs App ID + Secret + Config ID"}
                </span>
              </div>
              <p className="text-secondary mb-2" style={{ fontSize: 13 }}>
                New Meta apps need{" "}
                <a
                  href="https://developers.facebook.com/docs/facebook-login/facebook-login-for-business/"
                  target="_blank"
                  rel="noreferrer"
                >
                  Facebook Login for Business
                </a>{" "}
                (Config ID). Classic scopes cause “Invalid Scopes: pages_messaging”.
              </p>
              <div className="mb-3">
                <label className="form-label">OAuth redirect URI (Meta → Facebook Login → Settings)</label>
                <div className="input-group">
                  <input className="form-control" readOnly value={oauthCallbackUrl} />
                  <button
                    type="button"
                    className="btn btn-outline-secondary"
                    onClick={() => copyText(oauthCallbackUrl, "OAuth callback URL")}
                  >
                    Copy
                  </button>
                </div>
                {oauthAppDomain ? (
                  <small className="text-secondary d-block mt-1">
                    Also put <code>{oauthAppDomain}</code> in Meta → App settings → Basic → App Domains
                    (and Website Site URL = <code>https://{oauthAppDomain}/</code>).
                  </small>
                ) : null}
              </div>
              <div className="row g-3">
                <div className="col-md-6">
                  <label className="form-label">Facebook App ID</label>
                  <input
                    className="form-control"
                    value={form.facebook_app_id}
                    onChange={(e) => setField("facebook_app_id", e.target.value.trim())}
                    placeholder="From Meta → Settings → Basic"
                  />
                </div>
                <div className="col-md-6">
                  <label className="form-label">Facebook App Secret</label>
                  <div className="input-group">
                    <input
                      className="form-control"
                      type={showSecret ? "text" : "password"}
                      value={form.facebook_app_secret}
                      onChange={(e) => setField("facebook_app_secret", e.target.value.trim())}
                      placeholder="From Meta → Settings → Basic"
                      autoComplete="new-password"
                    />
                    <button
                      type="button"
                      className="btn btn-outline-secondary"
                      onClick={() => setShowSecret((v) => !v)}
                    >
                      {showSecret ? "Hide" : "Show"}
                    </button>
                  </div>
                </div>
                <div className="col-md-6">
                  <label className="form-label">Messenger Login Config ID</label>
                  <input
                    className="form-control"
                    value={form.facebook_login_config_id}
                    onChange={(e) => setField("facebook_login_config_id", e.target.value.trim())}
                    placeholder="Login for Business config (Pages / Messenger)"
                  />
                  <small className="text-secondary">
                    For Facebook / Instagram Connect.
                  </small>
                </div>
                <div className="col-md-6">
                  <label className="form-label">WhatsApp Embedded Signup Config ID</label>
                  <input
                    className="form-control"
                    value={form.whatsapp_login_config_id}
                    onChange={(e) => setField("whatsapp_login_config_id", e.target.value.trim())}
                    placeholder="Create from template: WhatsApp Embedded Signup"
                  />
                  <small className="text-secondary">
                    Meta → Facebook Login for Business → Create from template (WhatsApp Embedded Signup).
                  </small>
                </div>
                <div className="col-md-6">
                  <label className="form-label">Webhook verify token</label>
                  <div className="input-group">
                    <input
                      className="form-control"
                      value={form.facebook_verify_token}
                      onChange={(e) => setField("facebook_verify_token", e.target.value.trim())}
                      placeholder="Same token you enter in Meta Webhooks"
                    />
                    <button
                      type="button"
                      className="btn btn-outline-secondary"
                      onClick={() => {
                        const token = randomVerifyToken();
                        setField("facebook_verify_token", token);
                        setMsg("New verify token generated — click Save settings");
                      }}
                    >
                      Generate
                    </button>
                  </div>
                  <small className="text-secondary">
                    Use this exact value as Verify Token when adding Meta webhooks.
                  </small>
                </div>
              </div>
            </div>

            <div className="admin-form-actions">
              <button type="submit" className="admin-btn admin-btn-primary" disabled={saving}>
                {saving ? "Saving…" : "Save settings"}
              </button>
            </div>
          </form>
        </div>
      </div>
    </AdminLayout>
  );
};

export default AdminSettings;
